<script setup>
	import { reactive, computed } from "vue";

	const faqs = reactive([
		{ q: "I'm not technical. Can you still help me?", a: "Absolutely. We work with non-technical founders and small-business owners every day, and we explain everything in plain English — no jargon, no talking down to you.", open: true },
		{ q: "Can you fix software an AI or someone else built?", a: "Yes — that's a big part of what we do. We audit AI-built or inherited codebases, tell you honestly what's salvageable, then stabilize and clean them up so they're maintainable.", open: false },
		{ q: "How much does it cost?", a: "It depends on scope. Ongoing support starts around $800/month, and we handle everything from quick fixes to multi-year builds. New clients also get 50% off their first six weeks. Book a free consult and we'll give you a straight estimate.", open: false },
		{ q: "Who will be working on my project?", a: "A senior engineer leads every project and stays hands-on, supported by our experienced team. You're never handed off to someone learning on your dime.", open: false },
		{ q: "What kind of software do you build?", a: "Web apps, mobile apps, dashboards, booking platforms, internal tools, automations, and integrations — plus rescuing and modernizing existing codebases.", open: false },
		{ q: "Do you use offshore or nearshore teams?", a: "Our team is US-based. If you already work with an offshore or nearshore team, we're happy to collaborate with them.", open: false },
		{ q: "Do you charge to evaluate my needs?", a: "No. The first consultation and a high-level evaluation are always free.", open: false },
		{ q: "How quickly can you start?", a: "Usually within days, depending on how ready your project is. We'll map out the next steps on our first call.", open: false },
		{ q: "Do you use AI tools?", a: "Our engineers use AI tools where they genuinely help and where your environment allows it. If your organization doesn't permit them, we work just as effectively without them.", open: false },
		{ q: "Do I need to provide equipment or accounts?", a: "No — we can work on our own machines, or integrate with your existing infrastructure and access. Whatever's easiest for you.", open: false },
	]);

	function toggle(i) {
		faqs.forEach((f, idx) => (f.open = idx === i ? !f.open : false));
	}

	const faqLd = computed(() =>
		JSON.stringify({
			"@context": "https://schema.org",
			"@type": "FAQPage",
			mainEntity: faqs.map(f => ({
				"@type": "Question",
				name: f.q,
				acceptedAnswer: { "@type": "Answer", text: f.a },
			})),
		})
	);
</script>

<template>
	<div class="faq">
		<section class="hero">
			<div class="hero-inner">
				<span class="block-eyebrow">FAQ</span>
				<h1 class="hero-title">Questions, answered</h1>
				<p class="hero-lead">Straightforward answers about how we work, what it costs, and what to expect.</p>
			</div>
		</section>

		<section class="faq-list">
			<div class="faq-list-inner">
				<div class="faq-card">
					<ul class="faq-items">
						<li v-for="(item, i) in faqs" :key="i" class="faq-item">
							<button
								type="button"
								class="faq-q"
								:aria-expanded="item.open ? 'true' : 'false'"
								:aria-controls="`faq-panel-${i}`"
								@click="toggle(i)"
							>
								<span class="faq-q-text">{{ item.q }}</span>
								<span class="faq-toggle" aria-hidden="true"><i class="mdi" :class="item.open ? 'mdi-minus' : 'mdi-plus'"></i></span>
							</button>
							<div :id="`faq-panel-${i}`" class="faq-panel" :class="{ open: item.open }" role="region">
								<div class="faq-panel-clip">
									<p class="faq-answer">{{ item.a }}</p>
								</div>
							</div>
						</li>
					</ul>
				</div>

				<div class="cta-card">
					<h2 class="cta-title">Still have questions?</h2>
					<p class="cta-lead">Book a quick call and we'll map out scope, budget, and the best path forward.</p>
					<div class="cta-actions">
						<RouterLink :to="{ name: 'contact' }" class="btn btn-on-blue">Book a free consult</RouterLink>
						<RouterLink :to="{ name: 'services' }" class="btn btn-ghost-light">See pricing</RouterLink>
					</div>
				</div>
			</div>
		</section>

		<component :is="'script'" type="application/ld+json">{{ faqLd }}</component>
	</div>
</template>

<style scoped>
	.faq {
		--bg: #f5f9fd;
		--surface: #ffffff;
		--surface-muted: #eef4fb;
		--border: #e2ecf6;
		--border-strong: #cddcec;
		--navy: #0f2742;
		--ink: #16314f;
		--ink-soft: #4a5d75;
		--ink-muted: #7589a1;
		--sky: #0ea5e9;
		--sky-deep: #0284c7;
		--blue: #2563eb;
		--blue-deep: #1d4ed8;
		--blue-soft: #dceafe;

		font-family: "Poppins", "Plus Jakarta Sans", "Segoe UI", -apple-system, BlinkMacSystemFont, sans-serif;
		color: var(--ink);
		background: var(--bg);
		overflow-x: hidden;
	}

	.btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		padding: 0.95rem 1.9rem;
		border-radius: 12px;
		font-family: inherit;
		font-size: 1.05rem;
		font-weight: 700;
		text-decoration: none;
		cursor: pointer;
		border: 1px solid transparent;
		transition: transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
	}

	.btn-on-blue {
		background: #ffffff;
		color: var(--blue-deep);
		box-shadow: 0 14px 30px -14px rgba(0, 0, 0, 0.5);
	}

	.btn-on-blue:hover {
		transform: translateY(-2px);
		background: #f1f6ff;
		color: var(--blue-deep);
		box-shadow: 0 20px 36px -16px rgba(0, 0, 0, 0.55);
	}

	.btn-ghost-light {
		background: transparent;
		color: #ffffff;
		border-color: rgba(255, 255, 255, 0.45);
	}

	.btn-ghost-light:hover {
		transform: translateY(-2px);
		background: rgba(255, 255, 255, 0.12);
		border-color: #ffffff;
	}

	.hero {
		padding: clamp(3.5rem, 8vw, 6rem) 1.5rem clamp(2rem, 4vw, 3rem);
	}

	.hero-inner {
		max-width: 820px;
		margin: 0 auto;
		text-align: center;
	}

	.block-eyebrow {
		display: inline-block;
		font-size: 0.85rem;
		font-weight: 700;
		letter-spacing: 0.12em;
		text-transform: uppercase;
		color: var(--blue-deep);
		margin-bottom: 0.7rem;
	}

	.hero-title {
		font-size: clamp(2.2rem, 4.2vw, 3.4rem);
		line-height: 1.08;
		font-weight: 800;
		letter-spacing: -0.025em;
		color: var(--navy);
		margin: 0 0 1rem;
	}

	.hero-lead {
		font-size: clamp(1.08rem, 1.4vw, 1.28rem);
		line-height: 1.65;
		color: var(--ink-soft);
		max-width: 54ch;
		margin: 0 auto;
	}

	.faq-list {
		padding: clamp(1rem, 3vw, 2rem) 1.5rem clamp(3.5rem, 7vw, 6rem);
	}

	.faq-list-inner {
		max-width: 820px;
		margin: 0 auto;
	}

	.faq-card {
		background: var(--surface);
		border: 1px solid var(--border);
		border-radius: 20px;
		padding: 0.4rem clamp(1rem, 2.5vw, 1.6rem);
		box-shadow: 0 30px 60px -44px rgba(15, 39, 66, 0.45);
	}

	.faq-items {
		list-style: none;
		margin: 0;
		padding: 0;
	}

	.faq-item + .faq-item {
		border-top: 1px solid var(--border);
	}

	.faq-q {
		display: flex;
		align-items: center;
		gap: 1rem;
		width: 100%;
		text-align: left;
		background: transparent;
		border: 0;
		padding: 1.2rem 0.1rem;
		cursor: pointer;
		font-family: inherit;
	}

	.faq-q:focus-visible {
		outline: 3px solid rgba(37, 99, 235, 0.4);
		outline-offset: 2px;
		border-radius: 8px;
	}

	.faq-q-text {
		flex: 1 1 auto;
		font-size: clamp(1.02rem, 1.1vw, 1.18rem);
		font-weight: 700;
		color: var(--navy);
		line-height: 1.4;
	}

	.faq-toggle {
		flex-shrink: 0;
		width: 30px;
		height: 30px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		border-radius: 50%;
		background: var(--blue-soft);
		color: var(--blue-deep);
		transition: background 0.2s ease, color 0.2s ease;
	}

	.faq-q[aria-expanded="true"] .faq-toggle {
		background: var(--blue);
		color: #ffffff;
	}

	.faq-toggle .mdi {
		font-size: 18px;
		line-height: 1;
	}

	.faq-panel {
		display: grid;
		grid-template-rows: 0fr;
		transition: grid-template-rows 0.26s ease, margin 0.2s ease;
		margin: 0;
	}

	.faq-panel-clip {
		min-height: 0;
		overflow: hidden;
	}

	.faq-panel.open {
		grid-template-rows: 1fr;
		margin: 0 0 1rem;
	}

	.faq-answer {
		margin: 0;
		padding: 0.25rem 2.6rem 0.25rem 0.1rem;
		color: var(--ink-soft);
		line-height: 1.7;
		font-size: 1.04rem;
	}

	.cta-card {
		margin-top: 2rem;
		padding: clamp(2.5rem, 4vw, 3.5rem) clamp(1.75rem, 4vw, 3rem);
		border-radius: 24px;
		background: linear-gradient(135deg, var(--blue) 0%, var(--sky-deep) 100%);
		color: #ffffff;
		text-align: center;
		box-shadow: 0 34px 64px -36px rgba(37, 99, 235, 0.7);
	}

	.cta-title {
		font-size: clamp(1.7rem, 3vw, 2.4rem);
		font-weight: 800;
		line-height: 1.1;
		letter-spacing: -0.02em;
		margin: 0 auto 1rem;
		color: #ffffff;
	}

	.cta-lead {
		font-size: clamp(1.05rem, 1.3vw, 1.2rem);
		line-height: 1.6;
		color: rgba(255, 255, 255, 0.92);
		max-width: 50ch;
		margin: 0 auto 1.75rem;
	}

	.cta-actions {
		display: flex;
		gap: 1rem;
		justify-content: center;
		flex-wrap: wrap;
	}

	@media (max-width: 640px) {
		.cta-actions { flex-direction: column; align-items: stretch; }

		.btn { width: 100%; }

		.faq-answer { padding-right: 0.1rem; }
	}
</style>